import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import ConnectionIcon from 'app/common/presentation/Connection/ConnectionIcon'
import { useConnections } from 'app/connections/application'

import { Breadcrumb, Button, Headline, Link, Section } from 'components'

import { mapToConnectionName } from '../connectionUrlMapper'
import styles from './Connection.module.scss'

type ConnectionParams = {
  name: string
}

const Connection = () => {
  const { name } = useParams<ConnectionParams>() as ConnectionParams
  const navigate = useNavigate()
  const { t } = useTranslation('connections')

  const { availableConnections } = useConnections()

  const [connection] = useState(
    availableConnections.find((availableConn) => availableConn.name === mapToConnectionName(name)),
  )

  const docuLink = t(`available.${connection?.name}.docu`)

  return connection ? (
    <div className={styles.connection}>
      <Breadcrumb
        paths={[
          {
            name: t('overview.headline'),
            path: '/connections',
          },
          {
            name: t(`available.${connection.name}.displayName`),
          },
        ]}
      />
      <div className={styles.pageHeader}>
        <div>
          <div className={styles.icon}>
            <ConnectionIcon connection={connection} />
          </div>

          <div>
            <div>
              <Headline>{t(`available.${connection.name}.displayName`)}</Headline>
              <div>
                <div>Analytics</div>
                <div>Audiences</div>
              </div>
            </div>
            <div className={styles.provider}>
              <div>{t('details.provider')}</div>
              <Link
                title={t(`available.${connection.name}.provider.title`)}
                to={t(`available.${connection.name}.provider.link`)}
              />
            </div>
          </div>
        </div>
        <Button onClick={() => void navigate(`/connections/${name}/setup`)}>
          {t('details.connect')}
        </Button>
      </div>
      <div className={styles.info}>
        <div className={styles.resources}>
          {docuLink && (
            <div>
              <Section name={t('details.resources.title')} />
              <Link
                title={t('details.resources.docu')}
                to={t(`available.${connection.name}.docu`)}
              />
            </div>
          )}
          <div>
            <Section name={t('details.support.title')} />
            <Link title={t('details.support.mail')} to={t('details.support.mail')} type="mail" />
          </div>
        </div>
        <div className={styles.overview}>
          <Section name={t('details.overview')} />
          <div className={styles.description}>
            <Trans
              components={{ a: <a />, h3: <h3 />, li: <li />, ul: <ul /> }}
              i18nKey={`available.${connection.name}.description`}
              ns="connections"
            />
          </div>
        </div>
        <div className={styles.setup}>
          <Section name={t('details.setup')} />
          <div className={styles.description}>
            <Trans
              components={{ li: <li />, ul: <ul /> }}
              i18nKey={`available.${connection.name}.requirements`}
              ns="connections"
            />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div>{`Connection ${name} not found`}</div>
  )
}

export default Connection
